import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import { MetBody, MetLabel } from 'components/shared/common';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery, Theme } from '@mui/material';
import { EngagementListItem } from 'models/engagement';
import { When } from 'react-if';
import SurveyEmailsSent from 'components/shared/analytics/KPI/SurveyEmailsSent';
import SurveysCompleted from 'components/shared/analytics/KPI/SurveysCompleted';
import ProjectLocation from 'components/shared/analytics/KPI/ProjectLocation';
import SubmissionTrend from 'components/shared/analytics/charts/SubmissionTrend';
import SurveyBar from 'components/shared/analytics/charts/SurveyBar';
import { DashboardType } from 'constants/dashboardType';
import { Map } from 'models/analytics/map';

const EngagementsAccordion = ({
    engagements,
    bgColor,
    borderColor,
    disabled,
}: {
    engagements: EngagementListItem[];
    bgColor: string;
    borderColor: string;
    disabled?: boolean;
}) => {
    const [openedEngagements, setOpenedEngagements] = useState<number[]>([]);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
    const [projectMapData, setProjectMapData] = React.useState<Map | null>(null);
    const mapExists = projectMapData?.latitude !== null && projectMapData?.longitude !== null;

    if (engagements.length == 0) {
        return (
            <Grid item xs={12}>
                <MetLabel>No Engagements Found</MetLabel>
            </Grid>
        );
    }

    const handleChange = (engagementId: number) => {
        if (!openedEngagements.some((id) => id == engagementId)) {
            setOpenedEngagements([...openedEngagements, engagementId]);
        }
    };

    const handleProjectMapData = (data: Map) => {
        setProjectMapData(data);
    };

    return (
        <>
            {engagements.map((engagement) => {
                return (
                    <Accordion
                        disabled={disabled}
                        onChange={() => {
                            handleChange(engagement.id);
                        }}
                        key={engagement.id}
                        sx={{
                            mt: 1,
                            borderStyle: 'solid',
                            borderColor: borderColor,
                            boxShadow: 'none',
                            '&.Mui-disabled': {
                                background: 'initial',
                            },
                        }}
                    >
                        <AccordionSummary
                            data-testid={`engagement-accordion-${engagement.id}`}
                            sx={{
                                '&.Mui-disabled': {
                                    opacity: 1,
                                },
                                backgroundColor: bgColor,
                                borderBottom: `solid 1px ${borderColor}`,
                            }}
                        >
                            <Grid container direction={isMobile ? 'column' : 'row'}>
                                <Grid item md={7} sm={12}>
                                    <MetLabel>{engagement.name}</MetLabel>
                                </Grid>
                                <Grid item md={5} sm={12} textAlign={isMobile ? 'left' : 'right'}>
                                    <MetBody>
                                        {engagement.start_date}
                                        {' - '}
                                        {engagement.end_date}
                                    </MetBody>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            <When condition={openedEngagements.some((id) => id == engagement.id)}>
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    spacing={3}
                                    data-testid={`dashboard-frame-${engagement.id}`}
                                >
                                    <Grid item xs={12} sm={!mapExists ? 6 : 4}>
                                        <SurveyEmailsSent engagement={engagement} engagementIsLoading={false} />
                                    </Grid>
                                    <Grid item xs={12} sm={!mapExists ? 6 : 4}>
                                        <SurveysCompleted engagement={engagement} engagementIsLoading={false} />
                                    </Grid>
                                    <ProjectLocation
                                        engagement={engagement}
                                        engagementIsLoading={false}
                                        handleProjectMapData={handleProjectMapData}
                                    />
                                </Grid>
                                <Grid item xs={12} mt={2}>
                                    <SubmissionTrend engagement={engagement} engagementIsLoading={false} />
                                </Grid>
                                <Grid item xs={12} mt={2}>
                                    <SurveyBar
                                        engagement={engagement}
                                        engagementIsLoading={false}
                                        dashboardType={DashboardType.PUBLIC}
                                    />
                                </Grid>
                            </When>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );
};

export default EngagementsAccordion;
